import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from 'src/router.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES, ORDER_BY_NUMBER_QUERY_PARAM, PAGE_SIZE } from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useDeferredValue, useEffect, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import {
    ActuatorsPageData,
    ActuatorsPageVariables,
    getActuatorsPageConfig,
} from 'src/modules/actuators/ActuatorsPageMapping.ts';
import { skipToken } from '@apollo/client';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { ActuatorsFilterPagePrimaryQueryVariables, DirectionEnum } from 'src/generated/operations.ts';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer.tsx';
import { useUserInputState } from 'src/modules/user-input/useUserInputState.ts';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { useOrderBy } from 'src/modules/common/hooks/useOrderBy.ts';
import { usePageChange } from 'src/modules/common/hooks/usePageChange.ts';
import { generateOrderItems } from 'src/utils/generateOrderItems.ts';
import { Breadcrumbs } from 'src/modules/common/types/Breadcrumbs';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { Query } from 'src/modules/common/components/Query/Query';
import { DeviceGrid } from 'src/common/DeviceGrid/DeviceGrid';
import { Device } from 'src/common/Device/Device';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { prepareActuatorAttributes } from 'src/modules/actuators/ActuatorCard/prepareActuatorAttributes';
import clsx from 'clsx';
import { FilterSidebarComponent } from 'src/common/FilterSidebarComponent/FilterSidebarComponent';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler.ts';
import { FilterEventDetails } from 'src/modules/common/types/FilterEventDetails.ts';
import { ValvesBreadcrumbValue } from 'src/modules/common/components/ValvesBreadcrumbValue/ValvesBreadcrumbValue';
import { ValvesTooltipValue } from 'src/modules/common/components/ValvesTooltipValue/ValvesTooltipValue';
import { initLocationState } from 'src/modules/locationStateContext.ts';
import { useGetHomeBreadcrumb } from 'src/modules/home/useGetHomeBreadcrumb.ts';
import { ValveFilterTooltip } from 'src/modules/common/components/ValveFilterTooltip/ValveFilterTooltip.tsx';

const limit = PAGE_SIZE;

export const ActuatorsPage = () => {
    const { userSettings } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);
    const pageConfig = useMemo(() => getActuatorsPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }
    const { state: userInputState, locationState, setLocationState } = useUserInputState([]);

    const [
        searchParams,
        filter,
        checkboxHandler,,
        filtersReset,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.filterConfig,
    ]);

    const homePageValue = useGetHomeBreadcrumb({ filter });

    const navigate = useNavigate();

    const hasAdditionalFilter = useCheckMainFilter({
        searchParams,
        filter,
        navigate,
        filterConfig: pageConfig.filterConfig,
    });

    const { page, label } = useParams();

    if (!label) {
        throw new Error('No Valve selected');
    }
    if (!page) {
        throw new Error('No actuator page selected');
    }

    useEffect(() => {
        if (label !== locationState.valveLabel) {
            setLocationState({ ...locationState, valveLabel: label });
        }
        if (page !== locationState.actuatorsPage) {
            setLocationState({ ...locationState, actuatorsPage: page });
        }
    }, [label, page, locationState, setLocationState]);

    const breadcrumbProps: Breadcrumbs = [
        {
            title: MESSAGES.selectValveType,
            value: homePageValue,
            tooltipValue: <ValveFilterTooltip value={homePageValue}/>,
            state: { ...initLocationState },
            to: {
                pathname: PATHS.Home,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.RequestedFlow,
            value: <ValvesBreadcrumbValue
                is6WayRequired={locationState.is6WayRequired}
                region={userSettings.currentRegion}
                state={userInputState}
            />,
            tooltipValue: <ValvesTooltipValue
                is6WayRequired={locationState.is6WayRequired}
                region={userSettings.currentRegion}
                state={userInputState}
            />,
            state: { ...initLocationState },
            to: {
                pathname: PATHS.UserInput,
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedValve,
            value: locationState.valveLabel,
            state: locationState,
            to: {
                pathname: `${PATHS.Valves}/${locationState.valvesPage}`,
                search: searchParams.toString(),
            },
        },
        {
            selected: true,
            disabled: true,
            title: MESSAGES.SelectedActuator,
            value: locationState.actuatorLabel,
            actionShown: locationState.actuatorLabel.length > 2,
            onActionClick: () => {
                setLocationState({
                    ...locationState,
                    actuatorLabel: '-',
                });
            },
            to: '#',
        },
        {
            disabled: true,
            title: MESSAGES.SelectedAccessories,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map((item) => item.label).join(', ') : '-',
            to: '#',
        },
    ];

    const [
        ,
        actuatorsFilter,
    ] = useFilterLogic(pageConfig.filterConfig);

    const currentPage = useMemo(() => {
        const parsedPage = parseInt(page || '1', 10);
        return parsedPage < 1 ? 1 : parsedPage;
    }, [page]);

    const [orderByNumber, setOrderByNumber] = useOrderBy(`${ORDER_BY_NUMBER_QUERY_PARAM}_a`);

    const updatedLocation = useCallback((newPage: number) => {
        setLocationState({
            ...locationState,
            actuatorLabel: '',
            actuatorsPage: newPage.toString(),
            selectedAccessories: [],
            combo: null,
        });
    }, [setLocationState, locationState]);

    const onPageChange = usePageChange(
        (newPage: number) => `${PATHS.Actuators}/${encodeURIComponent(label)}/${newPage}`,
        updatedLocation,
    );

    const actuatorsCheckboxHandler: CheckboxHandler = useCallback((detail: FilterEventDetails) => {
        onPageChange(1, searchParams);
        return checkboxHandler(detail);
    }, [onPageChange, checkboxHandler, searchParams]);

    const resetActuatorsFilters = useMemo(() => () => {
        onPageChange(1, searchParams);
        filtersReset(pageConfig.filterConfig);
    }, [pageConfig, filtersReset, searchParams, onPageChange]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="back"
                state={locationState}
                to={{
                    pathname: `${PATHS.Valves}/${locationState.valvesPage}`,
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>

            <NavigationButton
                type="forward"
                eventData={{
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 4 - actuator parameters',
                    eventLabel: 'choose matching actuator',
                }}
                disabled={locationState.actuatorLabel.length < 2}
                state={locationState}
                to={{
                    pathname: [
                        PATHS.Accessories,
                        label,
                        encodeURIComponent(locationState.actuatorLabel),
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const variables = useMemo((): { variables: ActuatorsPageVariables } | typeof skipToken => {
        if (actuatorsFilter === null) {
            return skipToken;
        }

        const offset = (currentPage - 1) * limit;
        return {
            variables: {
                offset,
                limit,
                filter: composeFilter(
                    pageConfig.filterConfig,
                    actuatorsFilter,
                    userSettings.currentLocale,
                ),
                locale: userSettings.currentLocale,
                label,
                orderByNumber,
            },
        };
    }, [
        orderByNumber,
        currentPage,
        actuatorsFilter,
        label,
        userSettings,
        pageConfig,
    ]);

    const defVariables = useDeferredValue(variables);
    const isStale = defVariables !== variables;

    const filtersVariables = useMemo((): { variables: ActuatorsFilterPagePrimaryQueryVariables } | typeof skipToken => {
        if (actuatorsFilter === null) {
            return skipToken;
        }

        return {
            variables: {
                filter: composeFilter(
                    pageConfig.filterConfig,
                    actuatorsFilter,
                    userSettings.currentLocale,
                ),
                locale: userSettings.currentLocale,
                label,
                region: userSettings.currentRegion,
            },
        };
    }, [
        actuatorsFilter,
        label,
        userSettings,
        pageConfig,
    ]);

    return (
        <>
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.actuatorsPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={<BreadcrumbContainer isMobile={isMobile} breadcrumbs={breadcrumbProps}/>}
                />

                <Suspense fallback={<SkeletonPageLayout/>}>
                    <Query<ActuatorsPageData, ActuatorsPageVariables>
                        query={pageConfig.query}
                        variables={defVariables}
                    >
                        {({ data }) => {
                            if (data === undefined) {
                                return (
                                    <SkeletonPageLayout/>
                                );
                            }

                            if (data.vssActuators.products.length === 0) {
                                return (
                                    <div className={clsx({
                                        'vss-actuators-page': true,
                                        'vss-actuators-page--mobile': isMobile,
                                    })}
                                    >
                                        <FilterSidebarComponent
                                            label={MESSAGES.modalFilterActuatorsLabel}
                                            filterConfigPrimary={pageConfig.filterConfigPrimary}
                                            filterConfigSecondary={pageConfig.filterConfigSecondary}
                                            queryPrimary={pageConfig.queryPrimary}
                                            genericFilterQuery={pageConfig.genericFilterQuery}
                                            variables={filtersVariables}
                                            checkboxHandler={actuatorsCheckboxHandler}
                                            handleReset={resetActuatorsFilters}
                                            filter={filter}
                                        />
                                        <NoData/>
                                    </div>
                                );
                            }

                            return (
                                <div className={clsx({
                                    'vss-actuators-page': true,
                                    'vss-actuators-page--mobile': isMobile,
                                })}
                                >
                                    <FilterSidebarComponent
                                        label={MESSAGES.modalFilterActuatorsLabel}
                                        filterConfigPrimary={pageConfig.filterConfigPrimary}
                                        filterConfigSecondary={pageConfig.filterConfigSecondary}
                                        queryPrimary={pageConfig.queryPrimary}
                                        genericFilterQuery={pageConfig.genericFilterQuery}
                                        variables={filtersVariables}
                                        checkboxHandler={actuatorsCheckboxHandler}
                                        handleReset={resetActuatorsFilters}
                                        filter={filter}
                                    />

                                    <div className={clsx({
                                        'vss-actuators-page__list': true,
                                        'vss-actuators-page__list--stale': isStale,
                                    })}
                                    >
                                        <DeviceGrid
                                            isMobile={isMobile}
                                            hasAdditionalFilter={hasAdditionalFilter}
                                            currentPage={currentPage}
                                            pageSize={limit}
                                            total={data?.vssActuators.count || 0}
                                            onPageChange={(p: number) => onPageChange(p, searchParams)}
                                            defaultOrderLabel={MESSAGES.Label}
                                            orderItems={
                                                generateOrderItems(
                                                    [
                                                        'runningTime',
                                                        'torque',
                                                        'actuatingForce',
                                                    ],
                                                    pageConfig?.orderItemsConfig,
                                                    DirectionEnum.Asc,
                                                )
                                            }
                                            orderSelected={orderByNumber}
                                            onOrderByChange={(newOrder) => {
                                                onPageChange(1, setOrderByNumber(searchParams, newOrder));
                                            }}
                                        >
                                            {data?.vssActuators.products.map(({ product, type }) => (
                                                <Device
                                                    key={`${product.ID}_${product.Label}`}
                                                    defaultImage={<ActuatorIcon/>}
                                                    isSelected={locationState.actuatorLabel === product.Label}
                                                    product={product}
                                                    chip={type === 1 ? (
                                                        <div className="vss-device__recommendation">
                                                            <I18n messageId={MESSAGES.recommended}/>
                                                        </div>
                                                    ) : ''}
                                                    attributes={prepareActuatorAttributes(product)}
                                                    onDeviceClick={(details) => {
                                                        setLocationState({
                                                            ...locationState,
                                                            actuatorLabel: details.label,
                                                            selectedAccessories: [],
                                                            combo: null,
                                                        });
                                                    }}
                                                />
                                            ))}
                                        </DeviceGrid>
                                    </div>
                                </div>
                            );
                        }}
                    </Query>
                </Suspense>
            </MainLayout>

            {isMobile && navigation}
        </>
    );
};
